import type { JobListParams } from './list-params.js'

type JobStatus = JobListParams['status'][number]

/**
 * The four words the business actually uses for where a job is up to.
 *
 * ServiceM8 had exactly these — Quote, Work Order, Completed, Unsuccessful —
 * and they are what the office says out loud. `JobStatus` has ten values
 * instead, invented by the first schema commit rather than derived from
 * anything, and six of them have never been written by the application: the
 * loaded account contains only COMPLETED, APPROVED, SCHEDULED and QUOTING.
 *
 * So the stages are a reading of the enum, not a replacement for it. The URL
 * and the API still carry statuses, which keeps every existing link working
 * and leaves the sub-states available to the board, which does care about the
 * difference between booked and in progress.
 *
 * **These four are also the only ones anybody picks.** `sets` is the status a
 * pick writes, and the five it leaves out are not choices at all: `SCHEDULED`
 * is written by `schedule.createVisit` on a job already `APPROVED` (a booking
 * on a quote leaves it a quote), `APPROVED` by accepting a quote, and
 * `IN_PROGRESS` by a technician on site. Offering them beside the four made a
 * ten-item dropdown in which half the entries were reports of something that
 * had already happened, two of them reading "In Progress" and "In progress".
 * Decision 77.
 *
 * LEAD sits under Quote because that is where it comes from: the migrator
 * sends every status it does not recognise there, and `schedule.unscheduled`
 * already treats it as work that has not been sold yet.
 *
 * In `shared` so the job card's picker and the assistant's stage tool read
 * one declaration (decision 94). The Jobs list does not use these: as of
 * decision 35 it is three pipeline tabs with statuses derived from dates,
 * declared in `job-worklist.ts`, where `CANCELLED` reads as Closed / lost
 * inside the Quote tab rather than as a fourth stage.
 */
export const JOB_STAGES = [
  { key: 'QUOTE', label: 'Quote', sets: 'QUOTING', statuses: ['LEAD', 'QUOTING', 'QUOTED'] },
  {
    key: 'WORK_ORDER',
    label: 'Work Order',
    sets: 'APPROVED',
    statuses: ['APPROVED', 'SCHEDULED', 'IN_PROGRESS', 'ON_HOLD', 'AWAITING_PARTS'],
  },
  { key: 'COMPLETE', label: 'Complete', sets: 'COMPLETED', statuses: ['COMPLETED'] },
  { key: 'UNSUCCESSFUL', label: 'Unsuccessful', sets: 'CANCELLED', statuses: ['CANCELLED'] },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  sets: JobStatus
  statuses: readonly JobStatus[]
}>

export type JobStageKey = (typeof JOB_STAGES)[number]['key']

export const JOB_STAGE_KEYS = JOB_STAGES.map((stage) => stage.key) as [JobStageKey, ...JobStageKey[]]

/** Every status in the given stages, which is what the query filters on. */
export function statusesForStages(keys: readonly JobStageKey[]): JobStatus[] {
  return JOB_STAGES.filter((stage) => keys.includes(stage.key)).flatMap((stage) => [
    ...stage.statuses,
  ])
}

export function stageOf(status: string): JobStageKey | undefined {
  return JOB_STAGES.find((stage) => (stage.statuses as readonly string[]).includes(status))?.key
}

export function stageLabel(key: JobStageKey): string {
  return JOB_STAGES.find((stage) => stage.key === key)?.label ?? key
}

/**
 * The status moving a job to this stage writes.
 *
 * The stage a job is already on cannot be picked again — a `<select>` fires
 * nothing when the value does not change — so this never overwrites a live
 * sub-status with the stage's opening one. Moving a booked job to Quote and
 * back does reset it to `APPROVED`, which is the honest answer: it is a job
 * whose stage was changed twice.
 */
export function stageStatus(key: JobStageKey): JobStatus {
  const stage = JOB_STAGES.find((entry) => entry.key === key)
  if (!stage) throw new Error(`Unknown job stage: ${key}`)

  return stage.sets
}
